import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";

import CustomButton from "../../Components/CustomButton";
import CustomInput from "../../Components/CustomInput";
import { DashboardLayout } from "../../Components/Layout/DashboardLayout";
import ImageViewer from "../../Components/ImageViewer";
import Loader from '../../Components/Loader';
import CustomTable from "./../../Components/CustomTable";
import ProfileDisplay from "../../Components/ProfileDisplay";
import usePagination from "../../Hooks/usePagination";
import useSearch from "../../Hooks/useSearch";
import { useGetAllVendorAdsQuery, useApproveVendorAdMutation, useRejectVendorAdMutation, useRevokeBoostMutation } from "../../Redux/Apis/VendorAds";
import { dateFormatter } from "../../Utils";
import { toast } from "react-toastify";

import "./style.css";

const AD_SIZE_LABELS = {
  standard: 'Standard (1/8 Page)',
  bronze: 'Bronze (1/4 Page)',
  silver: 'Silver (1/3 Page)',
  gold: 'Gold (1/2 Page)',
  vip: 'VIP',
};

const SECTION_LABELS = {
  pros: 'The Pros',
  barn: 'The Barn',
  calcutta: 'Calcutta',
  greenskeeping: 'Greenskeeping',
};

const LEGACY_APPROVAL_STATUSES = new Set(['pending', 'approved', 'rejected']);

const getEffectiveAdStatus = (ad) => {
  const rawStatus = ad?.status;
  const hasEnded = ad?.endDate ? new Date(ad.endDate) < new Date() : false;

  if (rawStatus === 'cancelled') {
    return 'cancelled';
  }

  if (rawStatus === 'expired' || (rawStatus === 'active' && hasEnded)) {
    return 'expired';
  }

  if (rawStatus === 'active') {
    return 'active';
  }

  if (LEGACY_APPROVAL_STATUSES.has(rawStatus)) {
    return ad?.stripeSubscriptionId && !hasEnded ? 'active' : 'inactive';
  }

  if (rawStatus === 'inactive') {
    return 'inactive';
  }

  return ad?.stripeSubscriptionId && !hasEnded ? 'active' : 'inactive';
};

const getStatusMeta = (ad) => {
  switch (getEffectiveAdStatus(ad)) {
    case 'active':
      return { label: 'live', className: 'bg-success' };
    case 'inactive':
      return { label: 'needs attention', className: 'bg-warning text-dark' };
    case 'expired':
      return { label: 'ended', className: 'bg-secondary' };
    case 'cancelled':
      return { label: 'cancelled', className: 'bg-dark' };
    default:
      return { label: 'needs attention', className: 'bg-warning text-dark' };
  }
};

const sortValues = [
  {
    text: "Business Name",
    value: "businessName",
  },
  {
    text: "Created Date",
    value: "createdAt",
  }
];

const perPageValues = [
  {
    text: "10",
    value: 10,
  },
  {
    text: "20",
    value: 20,
  },
  {
    text: "50",
    value: 50,
  }
];

const VendorAds = () => {
  const [selectedAd, setSelectedAd] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Reject flow state. Rejecting an ad now sends the admin's note straight to the
  // vendor as the "Ad Needs Changes" notification body, so we collect that note in a
  // proper in-app modal (instead of the old browser prompt) and require real text —
  // an empty note would push a useless "fix it" message the vendor can't act on.
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [adToReject, setAdToReject] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const [sortBy, setSortBy] = useState(sortValues[1].value);

  const [search, debouncedSearch, onSearch] = useSearch();
  const { page, perPage, onNext, onPrevious } = usePagination();

  // Fetch all vendor ads
  const { data, isLoading, isFetching } = useGetAllVendorAdsQuery({
    page,
    limit: perPage,
    search: debouncedSearch,
    sortBy,
  });

  const handleViewDetails = (ad) => {
    setSelectedAd(ad);
    setShowDetailsModal(true);
  };

  const [approveVendorAd, { isLoading: isApproving }] = useApproveVendorAdMutation();
  const [rejectVendorAd, { isLoading: isRejecting }] = useRejectVendorAdMutation();
  const [revokeBoost, { isLoading: isRevoking }] = useRevokeBoostMutation();

  const handleApprove = async (ad) => {
    try {
      const res = await approveVendorAd(ad._id).unwrap();
      if (res?.paymentError) {
        toast.warn(res.message || 'Ad approved, but the payment could not be processed.');
      } else {
        toast.success(res?.message || 'Ad approved.');
      }
      setShowDetailsModal(false);
    } catch (e) {
      toast.error(e?.data?.message || 'Failed to approve ad');
    }
  };

  // Step 1 of reject: open the rationale modal. We don't call the API yet — the admin
  // first has to write what the vendor needs to fix.
  const handleReject = (ad) => {
    setAdToReject(ad);
    setRejectReason("");
    setShowRejectModal(true);
  };

  // Step 2 of reject: actually send it. The reason is required because the backend now
  // forwards it verbatim to the vendor as the "Ad Needs Changes" message body.
  const submitReject = async () => {
    const reason = rejectReason.trim();
    if (!reason) {
      toast.error('Please tell the vendor what needs to change before rejecting.');
      return;
    }
    try {
      const res = await rejectVendorAd({ adId: adToReject._id, reason }).unwrap();
      toast.success(res?.message || 'Ad rejected.');
      setShowRejectModal(false);
      setAdToReject(null);
      setRejectReason("");
      setShowDetailsModal(false);
    } catch (e) {
      toast.error(e?.data?.message || 'Failed to reject ad');
    }
  };

  const handleRevoke = async (ad) => {
    // Destructive: confirm first with a native browser dialog (boost revoke has no
    // extra detail to collect, so a full modal would be overkill here).
    if (!window.confirm("Revoke this ad's boost? The vendor's spotlight will be deactivated immediately.")) return;
    try {
      const res = await revokeBoost(ad._id).unwrap();
      toast.success(res?.message || 'Boost revoked.');
      setShowDetailsModal(false);
    } catch (e) {
      toast.error(e?.data?.message || 'Failed to revoke boost');
    }
  };

  useEffect(() => {
    document.title = 'The Turn At | Vendor Ads Management';
  }, []);

  const headers = [
    {
      key: "id",
      title: "Sr. No",
    },
    {
      key: "userName",
      title: "User Name",
    },
    {
      key: "businessName",
      title: "Business Name",
    },
    {
      key: "section",
      title: "Section",
    },
    {
      key: "adSize",
      title: "Ad Size",
    },
    {
      key: "status",
      title: "Status",
    },
    {
      key: "createdAt",
      title: "Submitted",
    },
    {
      key: "actions",
      title: "Actions",
    },
  ];

  return (
    <>
      <DashboardLayout>
        <div className="container-fluid">
          <div className="row mb-3">
            <div className="col-12">
              <div className="dashCard">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h2 className="mainTitle">Vendor Ads</h2>
                </div>

                <div className="row mb-5">
                  <div className="col-12">
                    <div className="table-section-header mb-3">
                      <h4 className="mb-0">
                        <span className="badge bg-primary me-2">All Vendor Ads</span>
                        {data?.total || 0} ads
                      </h4>
                    </div>

                    {(isLoading || isFetching) ? <Loader /> :
                      <div className="col-12">
                        <CustomTable
                          headers={headers}
                          length={data?.data?.length || 0}
                          page={page}
                          rowsPerPage={perPage}
                          onNext={onNext}
                          onPrevious={onPrevious}
                          total={data?.total}
                        >
                          <tbody>
                            {(data?.data || []).map((item, index) => {
                              const statusMeta = getStatusMeta(item);

                              return (
                                <tr key={item._id}>
                                  <td>{(index + 1) + ((page - 1) * perPage)}</td>
                                  <td className="text-capitalize">
                                    <ProfileDisplay
                                      name={item?.vendor?.name || 'Unknown'}
                                      image={item?.vendor?.picture}
                                    />
                                  </td>
                                  <td>{item?.vendor?.details?.businessName || item?.businessName || 'N/A'}</td>
                                  <td>
                                    <span className="badge bg-info text-dark">
                                      {SECTION_LABELS[item?.displaySection] || item?.displaySection}
                                    </span>
                                  </td>
                                  <td>
                                    <span className={`badge bg-${item?.adSize === 'gold' ? 'warning' : item?.adSize === 'silver' ? 'secondary' : item?.adSize === 'bronze' ? 'danger' : 'primary'}`}>
                                      {AD_SIZE_LABELS[item?.adSize] || item?.adSize}
                                    </span>
                                  </td>
                                  <td>
                                    {item?.reviewStatus === 'pending'
                                      ? <span className="badge bg-warning text-dark">pending review</span>
                                      : <span className={`badge ${statusMeta.className}`}>{statusMeta.label}</span>}
                                    {item?.boost?.active && (
                                      <span className="badge bg-warning text-dark ms-1">Boosted</span>
                                    )}
                                  </td>
                                  <td>{dateFormatter(item?.createdAt)}</td>
                                  <td>
                                    <CustomButton
                                      variant="primaryButton"
                                      text=""
                                      className="btn-sm"
                                      onClick={() => handleViewDetails(item)}
                                    >
                                      <FontAwesomeIcon icon={faEye} />
                                    </CustomButton>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </CustomTable>
                      </div>
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Ad Details Modal */}
        {showDetailsModal && selectedAd && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Ad Details</h5>
                  <button type="button" className="btn-close" onClick={() => setShowDetailsModal(false)}></button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <h6 className="text-muted">Vendor Information</h6>
                      <p><strong>User Name:</strong> {selectedAd.vendor?.name || 'Unknown'}</p>
                      <p><strong>Business Name:</strong> {selectedAd.vendor?.details?.businessName || selectedAd.businessName || 'N/A'}</p>
                      <p><strong>Description:</strong> {selectedAd.businessInfo}</p>
                      <p><strong>Email:</strong> {selectedAd.contactEmail}</p>
                      <p><strong>Phone:</strong> {selectedAd.contactPhone || 'N/A'}</p>
                      <p><strong>Website:</strong> {selectedAd.website || 'N/A'}</p>
                    </div>
                    <div className="col-md-6">
                      <h6 className="text-muted">Ad Configuration</h6>
                      <p><strong>Section:</strong> {SECTION_LABELS[selectedAd.displaySection]}</p>
                      <p><strong>Size:</strong> {AD_SIZE_LABELS[selectedAd.adSize]}</p>
                      <p><strong>Category:</strong> {selectedAd.category?.name || 'N/A'}</p>
                    </div>
                  </div>
                  <hr />
                  <h6 className="text-muted">Ad Content</h6>
                  <p><strong>Headline:</strong> {selectedAd.adText?.headline}</p>
                  <p><strong>Description:</strong> {selectedAd.adText?.description}</p>
                  <p><strong>Call to Action:</strong> {selectedAd.adText?.callToAction}</p>
                  {selectedAd.adImages?.length > 0 && (
                    <>
                      <h6 className="text-muted mt-3">Images</h6>
                      <div className="d-flex gap-2 flex-wrap">
                        {selectedAd.adImages.map((img, idx) => (
                          <ImageViewer
                            key={idx}
                            src={img.url?.startsWith('http') ? img.url : `${process.env.REACT_APP_IMAGE_ENDPOINT || ''}${img.url}`}
                            image_style={{ maxWidth: '150px', maxHeight: '150px', objectFit: 'cover', borderRadius: '8px' }}
                            image_classname="me-2 mb-2"
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  {selectedAd.reviewStatus === 'pending' && (
                    <>
                      <button type="button" className="btn btn-success" disabled={isApproving || isRejecting} onClick={() => handleApprove(selectedAd)}>
                        {isApproving ? 'Approving…' : 'Approve'}
                      </button>
                      <button type="button" className="btn btn-danger" disabled={isApproving || isRejecting} onClick={() => handleReject(selectedAd)}>
                        {isRejecting ? 'Rejecting…' : 'Reject'}
                      </button>
                    </>
                  )}
                  {selectedAd?.boost?.active && (
                    <button
                      type="button"
                      className="btn btn-warning text-dark"
                      data-testid="revoke-boost-btn"
                      disabled={isRevoking || isApproving || isRejecting}
                      onClick={() => handleRevoke(selectedAd)}
                    >
                      {isRevoking ? 'Revoking…' : 'Revoke Boost'}
                    </button>
                  )}
                  <button type="button" className="btn btn-secondary" onClick={() => setShowDetailsModal(false)}>Close</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reject Ad Modal — collects the note that gets pushed to the vendor as
            "Ad Needs Changes". Styled with the same inline-bootstrap pattern as the
            details modal above so it looks native to this screen. */}
        {showRejectModal && adToReject && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Reject Ad</h5>
                  <button type="button" className="btn-close" disabled={isRejecting} onClick={() => setShowRejectModal(false)}></button>
                </div>
                <div className="modal-body">
                  <p className="text-muted mb-3">
                    Tell the vendor what needs to change. This note is sent to them as the
                    reason their ad needs edits, so be specific and actionable.
                  </p>
                  <CustomInput
                    id="rejectReason"
                    type="textarea"
                    label="Required edits / reason"
                    placeholder="e.g. The headline mentions a competitor — please remove it and resubmit."
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    labelClass="mainLabel"
                    inputClass="mainInput"
                    required
                  />
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" disabled={isRejecting} onClick={() => setShowRejectModal(false)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    data-testid="confirm-reject-btn"
                    disabled={isRejecting || !rejectReason.trim()}
                    onClick={submitReject}
                  >
                    {isRejecting ? 'Rejecting…' : 'Reject Ad'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </DashboardLayout>
    </>
  );
};

export default VendorAds;
